import { ChangeEventHandler, KeyboardEventHandler, useRef, useState } from 'react'
import { Transition } from 'react-transition-group'
import { i18n } from 'builder/utils/i18n'
import {
  convertToExternal,
  convertToFieldValue,
  convertToInternal,
  guessMonth,
  guessYear,
  isDateHidden,
  isMonthHidden,
  isPresent,
} from 'builder/components/DateRangePicker/utils'

import PickerPanel from 'builder/components/DateRangePicker/PickerPanel'
import { TextField } from 'builder/components/TextField'

import {
  Container,
  Label,
  LabelContent,
  LabelHint,
} from 'builder/components/DateRangePicker/styles'
import { DateRangePickerProps } from 'builder/components/DateRangePicker/types'
import { useClickOutside } from 'builder/hooks/useClickOutside'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'

type Props = DateRangePickerProps & {
  headingLabel?: string
  showLabelHint?: boolean
}

export const AcquisitionDatePicker = (props: Props) => {
  const { showLabelHint = true, value, onChange = () => {}, currentlyLabel, headingLabel } = props
  const [endValue, setEndValue] = useState<string | null>(null)
  const [endMonth, setEndMonth] = useState<number | null>(null)
  const [endYear, setEndYear] = useState(0)
  const [isEndPickerOpen, setIsEndPickerOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)
  const transitionRef = useRef(null)

  useEffectOnMount(() => {
    const [endValue, endMonth, endYear] = convertToInternal(value.dateUntil, {
      isDatePresent: value.isDateUntilPresent,
      isMonthHidden: value.isMonthUntilHidden,
    })

    setEndValue(endValue)
    setEndMonth(endMonth)
    setEndYear(endYear)
  })

  useClickOutside(container, () => {
    setIsEndPickerOpen(false)
  })

  const handleEnter = () => {}

  const handleEntering = () => {}

  const handleExiting = () => {}

  const openEndPicker = () => {
    setIsEndPickerOpen(true)
  }

  const closeEndPicker = () => {
    setIsEndPickerOpen(false)
  }

  const changeEndYear = (endYear: number) => {
    const endValue = convertToFieldValue(endMonth, endYear)

    setEndYear(endYear)
    setEndValue(endValue)

    onChange({
      dateUntil: convertToExternal(endMonth, endYear, {
        isDateHidden: isDateHidden(endValue),
      }),
    })
  }

  const updateEndPicker: ChangeEventHandler<HTMLInputElement> = e => {
    const newValue = e.target.value

    const newState = {
      endValue: newValue,
      endMonth: guessMonth(newValue),
      endYear: guessYear(newValue),
    }

    setEndValue(newState.endValue)
    setEndMonth(newState.endMonth)
    setEndYear(newState.endYear)

    onChange({
      dateUntil: convertToExternal(newState.endMonth, newState.endYear, {
        isDateHidden: isDateHidden(newState.endValue),
      }),
      isDateUntilPresent: isPresent(newState.endValue),
      isMonthUntilHidden: isMonthHidden(newState.endValue),
    })
  }

  const handleEndChoose = (month: number | null, year: number) => {
    setEndMonth(month)
    setEndValue(convertToFieldValue(month, year))
    setIsEndPickerOpen(false)

    onChange({
      dateUntil: convertToExternal(month, year),
      isMonthUntilHidden: !month,
    })
  }

  const handlePresentToggle = (value: boolean) => {
    setEndValue(
      convertToFieldValue(endMonth, endYear, {
        isDatePresent: value,
      }),
    )

    onChange({
      isDateUntilPresent: value,
    })
  }

  const handleEndBlur = () => {
    setEndValue(convertToFieldValue(endMonth, endYear, { prevValue: endValue }))

    onChange({
      dateUntil: convertToExternal(endMonth, endYear, {
        isDateHidden: isDateHidden(endValue),
      }),
      isDateUntilPresent: isPresent(endValue),
      isMonthUntilHidden: isMonthHidden(endValue),
    })
  }

  const handleTab = () => {
    if (isEndPickerOpen) closeEndPicker()
  }

  const handleKeyDown: KeyboardEventHandler = e => {
    let keyCode = e.keyCode

    // tab
    if (keyCode === 9) {
      handleTab()
    }
  }

  return (
    <Container onKeyDown={handleKeyDown} ref={container}>
      <Label>
        <LabelContent>{headingLabel}</LabelContent>
        {showLabelHint && (
          <LabelHint multiline>{i18n.t('builder.date_range_picker.hint')}</LabelHint>
        )}
      </Label>
      <TextField
        name="endDate"
        onFocus={openEndPicker}
        onBlur={handleEndBlur}
        onChange={updateEndPicker}
        value={endValue || ''}
        active={isEndPickerOpen}
        placeholder={i18n.t('builder.date_range_picker.placeholder')}
        autoComplete="off"
      />

      <Transition
        in={isEndPickerOpen}
        timeout={0}
        onEnter={handleEnter}
        onEntering={handleEntering}
        onExiting={handleExiting}
        unmountOnExit
        appear
        nodeRef={transitionRef}
      >
        <PickerPanel
          month={endMonth}
          year={endYear}
          onChangeYear={changeEndYear}
          onChoose={handleEndChoose}
          showIsPresent
          isPresent={isPresent(endValue)}
          onIsPresentToggle={handlePresentToggle}
          alignRight
          currentlyLabel={currentlyLabel}
          noActiveMonth={isDateHidden(endValue) || isMonthHidden(endValue)}
          containerRef={transitionRef}
        />
      </Transition>
    </Container>
  )
}
